import { CommonModule } from "@angular/common";
import { Component, computed, inject, Input, input, OnInit, output, signal } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../utils/no-whitespace.validator';
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { IndicationQuoteStepService } from "../../services/indicationQuoteStep.service";
import { HttpResponse } from "@angular/common/http";
import { GetUserDetails } from "../../models/sessionStorage.model";
import { AuthService } from "../../auth/auth.service";
import {MatButtonModule} from '@angular/material/button';
import { MatDialog,MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { QuoteComparisonComponent } from "../quote-comparison/quote-comparison.component";
import { ToastService } from "../../services/toast.service";
import { EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS, ToastType } from "../../enums/config.enum";
import { BrokerQuoteService } from "../../services/brokerQuote.service";
import { ConfirmationDialogComponent } from "../utils/confirmation-dialog/confirmation-dialog.component";
import { QuoteDocumentDialogComponent } from "../utils/quote-document-dialog/quote-document-dialog.component";
import { PolicyAdministrationService } from "../../services/policyAdministration.service";
import { QuoteDocumentService } from "../../services/quoteDocument.service";
import { QuoteDocuments } from "../quote-documents/quote-documents.component";
import { RequestQuoteService } from "../../services/requestQuote.service";
import { NavigationHandlerService } from "../../services/navigationHandler.service";
import { forkJoin, Observable, of, Subscription } from "rxjs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";
import { PropertyService } from "../../services/property.service";
import { SpinnerComponent } from "../utils/spinner/spinner.component";


@Component({
    selector: 'app-accept-quote-step',
    standalone: true,
    imports: [MatExpansionModule, MatSnackBarModule, MatFormFieldModule, MatInputModule,CommonModule, FormsModule, ReactiveFormsModule,MatCheckboxModule, MatSelectModule, QuoteDocuments, SpinnerComponent],
    templateUrl: './accept-quote-step.component.html',
    styleUrl: './accept-quote-step.component.scss',
})
export class AcceptQuoteStepComponent implements OnInit{
    userId = signal<number | null>(null);
    indicationQuoteId = signal<string | null>(null);
    requestBindStep = signal(3);
     isLoading = signal(false);
    proceedToRequestBindStep = signal(false);
    proceedToNextStep = output<number>();
    requestBindEmitter = output<boolean>();
    amendQuoteEmitter = output<boolean>();
    quoteIssueEmitter = output<boolean>();
    targetPremiumEmitter = output<boolean>();
    saveIndicationHomeEmitter = output<any>();
    enableViewAndDownloadDocumentSignal = signal(false);
    isDownloadEmailComplete = signal<number>(0);
    enableViewAndDownloadDocument = computed(() => 
        this.enableViewAndDownloadDocumentSignal() && this.isDownloadEmailComplete() !== 1
    );
    quoteResponseMessage = signal<string>('');
    versionNo = signal<number | null>(null);
    isActionFromPopup = signal<boolean>(false);
    indicationId = signal<string | null>('');
    quoteId = signal<string | null>('');
    branchId = signal<string | null>('');
    storageData = signal<GetUserDetails | null>(null);
    authService = inject(AuthService);
    indicationQuoteService = inject(IndicationQuoteStepService);
    toastService = inject(ToastService);
    requestQuoteService = inject(RequestQuoteService);    
    fb: FormBuilder = inject(FormBuilder);
    readonly dialog = inject(MatDialog);
    router = inject(Router);
    brokerQuoteService = inject(BrokerQuoteService);
    propertyService = inject(PropertyService);
    policyService = inject(PolicyAdministrationService);
    quoteDocumentService = inject(QuoteDocumentService);
    indicationRiskSummaryDetailsEmitter = output<any>();
    isInceptionReferralLock = signal<boolean>(false);
    isUWAdmin = signal<boolean>(false);
    navigationHandler = inject(NavigationHandlerService);
    navLogoClickSubscription: any;
    private addEquipmentBreakdownSubscription?: Subscription;
    private pendingResetFeesOnSurplusLoadDueToTargetPremium = false;
    private loadingCounter = 0;
    surplusLinesTaxFeeOptions = signal<any[]>([]);
    maxSurplusFeeItems = 5;
    maxSurplusFeeAmount = 2500;
    /** Allows 0–2 decimal places; `\.\d{0,2}` supports values like `12.` while the user is typing. */
    private readonly currencyAmountPattern = /^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{0,2})?$/;
    isReadOnly = signal(false);
    policyFeeGencodeId = signal<number | null>(null);
    surplusLinesTaxGencodeId = signal<number | null>(null);
    eqbdOccupancyDetails = signal<any>(null);
    eqbdAdditionalPremium = signal<number>(50);
    commercialPropertyPremium = signal<number>(0);
    inspectionFeeFromCoverage = signal<number>(0);
    isEqbdDeclined = signal<boolean>(false);
    isEqbdDisabledDueToDeclineRule = signal<boolean>(false);
    isEqbdDisabledDueToHighBpp = signal<boolean>(false);
    isEqbdDisabledDueToUWDecision = signal<boolean>(false);
    eqbdCoverageId = signal<number>(0);
    taxFeesRecalculatePolicyFee = signal(false);
    taxFeesRecalculateSurplusTax = signal(false);
    taxFeesRecalculateSltfRows = signal<boolean[]>([]);
    /** Set only when reset is due to EQBD checkbox or targetPremium mismatch — controls "Please recalculate" copy. */
    taxFeesRecalculatePromptSource = signal<'checkbox' | 'targetPremium' | null>(null);
    acceptAndIssueForm = this.fb.group({
        addEquipmentBreakdown: [false],
        policyFee: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator(), this.nonZeroFeeAmountValidator()]],
        surplusLinesTax: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator(), this.nonZeroFeeAmountValidator()]],
        slTaxFeeDetails: this.fb.array([]),
        termsAndCondition: [false, Validators.requiredTrue],
        MaterialFactsStatement: [false, Validators.requiredTrue],
        TaxNoticeStatement: [false, Validators.requiredTrue],
        ClaimsDamageStatement: [false, Validators.requiredTrue]
    });
    get slTaxFeeItems(): FormArray {
        return this.acceptAndIssueForm.get('slTaxFeeDetails') as FormArray;
    }

    private startLoading(): void {
        this.loadingCounter += 1;
        this.isLoading.set(true);
    }

    private stopLoading(): void {
        this.loadingCounter = Math.max(0, this.loadingCounter - 1);
        if (this.loadingCounter === 0) {
            this.isLoading.set(false);
        }
    }

    hasReachedMaxFeeItems(): boolean {
        return false; // No maximum limit - users can add unlimited items
    }

    private parseAmount(value: any): number {
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        const numeric = parseFloat(String(value).replace(/,/g, ''));
        return isNaN(numeric) ? 0 : numeric;
    }

    private feeAmountMaxValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = control.value;
            if (value === null || value === undefined || value === '') {
                return null;
            }
            const numeric = this.parseAmount(value);
            return numeric > this.maxSurplusFeeAmount ? { feeMaxExceeded: true } : null;
        };
    }

    private nonZeroFeeAmountValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = control.value;
            if (value === null || value === undefined || value === '') {
                return null;
            }
            const numeric = this.parseAmount(value);
            if (isNaN(numeric) || numeric <= 0) {
                return { nonZero: true };
            }
            return null;
        };
    }

    hasAnyTaxFeesRecalculateMessage(): boolean {
        return (
            this.taxFeesRecalculatePolicyFee()
            || this.taxFeesRecalculateSurplusTax()
            || this.taxFeesRecalculateSltfRows().some(Boolean)
        );
    }

    shouldShowTaxFeesRecalculatePrompt(): boolean {
        return !this.isIndicationType() && !this.authService.isPolicyType() && !!this.taxFeesRecalculatePromptSource();
    }

    private isOtherFeeOptionId(optionId: number): boolean {
        const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(optionId));
        if (!option) {
            return false;
        }
        const description = option.description?.toLowerCase();
        const code = option.code?.toLowerCase();
        return description === 'other' || code === 'other';
    }

    createTaxFeeGroup(detail: any = {}): FormGroup {
        const feeAmountValue = detail?.feeAmount !== undefined && detail?.feeAmount !== null ? detail?.feeAmount : '';
        const hasSlFeeId = detail?.slFeeId && detail?.slFeeId !== '';
        
        const group = this.fb.group({
            id: [detail?.id ?? 0],
            slFeeId: [detail?.slFeeId ?? ''],
            slFeeDescription: [detail?.slFeeDescription ?? ''],
            otherDescription: [detail?.otherDescription ?? ''],
            feeAmount: [
                feeAmountValue !== '' ? String(feeAmountValue) : '',
                hasSlFeeId 
                    ? [Validators.required, noWhitespaceValidator, Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator(), this.nonZeroFeeAmountValidator()]
                    : [Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]
            ]
        });
        
        // Amount is required only when description is selected
        const slFeeIdControl = group.get('slFeeId');
        const feeAmountControl = group.get('feeAmount');
        
        if (slFeeIdControl && feeAmountControl) {
            slFeeIdControl.valueChanges.subscribe((value) => {
                if (value && value !== '') {
                    feeAmountControl.setValidators([Validators.required, noWhitespaceValidator, Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator(), this.nonZeroFeeAmountValidator()]);
                } else {
                    feeAmountControl.setValidators([Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]);
                }
                feeAmountControl.updateValueAndValidity({ emitEvent: false });
            });
        }
        
        return group;
    }

    isFeeSectionDisabled(): boolean {
        return this.acceptAndIssueForm.disabled
            || this.isInceptionReferralLock()
            || this.authService.isPolicyType()
            || !!this.acceptAndIssueForm.get('termsAndCondition')?.disabled;
    }

    allowCurrencyInput(event: KeyboardEvent): void {
        const key = event.key;
        const allowedPattern = /^[0-9.]$/;
        if (key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'ArrowLeft' || key === 'ArrowRight') {
            return;
        }
        if (!allowedPattern.test(key)) {
            event.preventDefault();
            return;
        }
        if (key === '.') {
            const currentValue = (event.target as HTMLInputElement).value;
            if (currentValue.includes('.')) {
                event.preventDefault();
            }
        }
    }

    /**
     * Keeps one decimal point and up to 2 fractional digits; preserves a trailing `.` so decimals can be entered
     * (parseFloat('12.') === 12 would otherwise strip the dot on each input event).
     */
    private applyCurrencyAmountInputFormatting(
        event: Event,
        setControlValue: (v: string) => void,
        onPositiveAmountClear?: () => void
    ): void {
        const input = event.target as HTMLInputElement;
        let raw = input.value.replace(/[^0-9.]/g, '');
        const firstDot = raw.indexOf('.');
        if (firstDot !== -1) {
            const intPart = raw.slice(0, firstDot);
            let fracPart = raw.slice(firstDot + 1).replace(/\./g, '');
            const hadTrailingDot = raw.endsWith('.') && fracPart === '';
            fracPart = fracPart.slice(0, 2);
            if (hadTrailingDot && fracPart === '') {
                raw = intPart + '.';
            } else if (fracPart.length > 0) {
                raw = intPart + '.' + fracPart;
            } else {
                raw = intPart;
            }
        }
        if (raw.startsWith('.')) {
            raw = '0' + raw;
        }
        if (raw === '') {
            setControlValue('');
            input.value = '';
            return;
        }
        if (raw.endsWith('.')) {
            const intPart = raw.slice(0, -1);
            if (intPart === '') {
                input.value = '0.';
                setControlValue('0.');
                return;
            }
            const intNum = parseFloat(intPart.replace(/,/g, ''));
            if (isNaN(intNum)) {
                setControlValue('');
                input.value = '';
                return;
            }
            const display =
                new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(intNum) + '.';
            setControlValue(intPart + '.');
            input.value = display;
            if (intNum > 0 && onPositiveAmountClear) {
                onPositiveAmountClear();
            }
            return;
        }
        // Preserve fractional digits as typed (e.g. 12.0, 12.00); parseFloat + Intl would collapse to "12".
        if (raw.includes('.') && !raw.endsWith('.')) {
            const dotIdx = raw.indexOf('.');
            const intPart = raw.slice(0, dotIdx);
            const fracPart = raw.slice(dotIdx + 1);
            const intNum = parseFloat(intPart.replace(/,/g, ''));
            if (isNaN(intNum)) {
                setControlValue('');
                input.value = '';
                return;
            }
            const intDisplay = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(intNum);
            const display = intDisplay + '.' + fracPart;
            setControlValue(raw);
            input.value = display;
            const num = parseFloat(raw.replace(/,/g, ''));
            if (!isNaN(num) && num > 0 && onPositiveAmountClear) {
                onPositiveAmountClear();
            }
            return;
        }
        const num = parseFloat(raw.replace(/,/g, ''));
        if (isNaN(num)) {
            setControlValue('');
            input.value = '';
            return;
        }
        setControlValue(String(num));
        input.value = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(num);
        if (num > 0 && onPositiveAmountClear) {
            onPositiveAmountClear();
        }
    }

    formatFeeAmount(event: any, index: number): void {
        this.applyCurrencyAmountInputFormatting(
            event,
            (v) => {
                const feeGroup = this.slTaxFeeItems.at(index) as FormGroup;
                const feeAmountControl = feeGroup.get('feeAmount');
                if (feeAmountControl) {
                    feeAmountControl.setValue(v, { emitEvent: false });
                    feeAmountControl.updateValueAndValidity({ emitEvent: false });
                }
            },
            () => {
                this.clearTaxFeesRecalculateSltfRow(index);
                this.syncRecalculatePromptSourceIfCleared();
            }
        );
    }

    formatPolicyFeeAmount(event: any): void {
        this.applyCurrencyAmountInputFormatting(
            event,
            (v) => {
                const control = this.acceptAndIssueForm.get('policyFee');
                if (control) {
                    control.setValue(v, { emitEvent: false });
                    control.updateValueAndValidity({ emitEvent: false });
                }
            },
            () => {
                this.taxFeesRecalculatePolicyFee.set(false);
                this.syncRecalculatePromptSourceIfCleared();
            }
        );
    }

    formatSurplusLinesTaxAmount(event: any): void {
        this.applyCurrencyAmountInputFormatting(
            event,
            (v) => {
                const control = this.acceptAndIssueForm.get('surplusLinesTax');
                if (control) {
                    control.setValue(v, { emitEvent: false });
                    control.updateValueAndValidity({ emitEvent: false });
                }
            },
            () => {
                this.taxFeesRecalculateSurplusTax.set(false);
                this.syncRecalculatePromptSourceIfCleared();
            }
        );
    }

    private getEQBDContext(): { id: number; type: string } | null {
        const userDetails = this.authService.getUserDetailsFromStorage();
        if (!userDetails) {
            return null;
        }

        if (userDetails.isPolicyType && userDetails.policyId) {
            return { id: Number(userDetails.policyId), type: 'Policy' };
        }

        if (userDetails.quoteType === 'indication' && userDetails.indicationId) {
            return { id: Number(userDetails.indicationId), type: 'Indication' };
        }

        if (userDetails.quoteId) {
            return { id: Number(userDetails.quoteId), type: 'Quote' };
        }

        return null;
    }

    private setEqbdDetails(response: any): void {
        const commercialProperty = Number(response?.commercialProperty ?? 0);
        const items = Array.isArray(response?.eqbdOccupancyDetails)
            ? response.eqbdOccupancyDetails
            : (Array.isArray(response) ? response : []);

        const hasDeclineRule = items.some((item: any) => String(item?.rule || '').toLowerCase() === 'decline');
        const restrictedDescriptions = new Set(EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS.map((desc) => desc.toLowerCase()));
        const hasRestrictedDescriptionWithHighBpp = items.some((item: any) => {
            const occupancyDescription = String(item?.occupancyDecription || item?.description || '').trim().toLowerCase();
            const bppLimit = Number(item?.bppLimit ?? 0);
            return restrictedDescriptions.has(occupancyDescription) && bppLimit > 499999;
        });
        const totalPremium = items.reduce((sum: number, item: any) => {
            const value = Number(item?.eqbdCalcPremium ?? 0);
            return sum + (isNaN(value) ? 0 : value);
        }, 0);

        this.commercialPropertyPremium.set(isNaN(commercialProperty) ? 0 : commercialProperty);
        this.isEqbdDisabledDueToDeclineRule.set(hasDeclineRule);
        this.isEqbdDisabledDueToHighBpp.set(hasRestrictedDescriptionWithHighBpp);
        this.eqbdAdditionalPremium.set(totalPremium < 50 ? 50 : totalPremium);
        this.updateEqbdAccordionDisableState();

        if (this.isEqbdDeclined()) {
            this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: false }, { emitEvent: false });
        }
    }

    private updateEqbdAccordionDisableState(): void {
        const shouldDisableEqbd =
            this.isEqbdDisabledDueToDeclineRule()
            || this.isEqbdDisabledDueToHighBpp()
            || this.isEqbdDisabledDueToUWDecision();
        this.isEqbdDeclined.set(shouldDisableEqbd);
    }

    loadEQBDOccupancyDetails(showLoader: boolean = false): void {
        const context = this.getEQBDContext();
        if (!context || !context.id) {
            return;
        }
        if (showLoader) {
            this.startLoading();
        }

        this.indicationQuoteService.GetEQBDOccupanyDetails(context.id, context.type).subscribe({
            next: (response: any) => {
                this.eqbdOccupancyDetails.set(response.data.eqbdOccupancyDetails ?? null);
                this.setEqbdDetails(response?.data ?? response);
                if (showLoader) {
                    this.stopLoading();
                }
            },
            error: () => {
                this.eqbdOccupancyDetails.set(null);
                this.isEqbdDisabledDueToDeclineRule.set(false);
                this.isEqbdDisabledDueToHighBpp.set(false);
                this.eqbdAdditionalPremium.set(50);
                this.commercialPropertyPremium.set(0);
                this.updateEqbdAccordionDisableState();
                if (showLoader) {
                    this.stopLoading();
                }
            }
        });
    }

    loadEQBDReferralDetails(showLoader: boolean = false): void {
        const context = this.getEQBDContext();
        if (!context || !context.id || context.type === 'Policy') {
            return;
        }
        if (showLoader) {
            this.startLoading();
        }
        const type = context.type.toLowerCase();
        this.indicationQuoteService.GetEQBDReferralDetails(context.id, type).subscribe({
            next: (response: any) => {
                const records = Array.isArray(response?.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);

                const currentVersionNo = Number(sessionStorage.getItem("versionNo"));
                const currentVersionRecord = records.find(
                    (item: any) => Number(item?.versionNo) === currentVersionNo
                );
                const availability = currentVersionRecord?.isEQBDAvailabe;
                const isDisabledByUW = availability !== null
                    && availability !== undefined
                    && availability !== ''
                    && Number(availability) === 0;

                this.isEqbdDisabledDueToUWDecision.set(isDisabledByUW);
                this.updateEqbdAccordionDisableState();
                if (this.isEqbdDeclined()) {
                    this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: false }, { emitEvent: false });
                }
                if (showLoader) {
                    this.stopLoading();
                }
            },
            error: () => {
                this.isEqbdDisabledDueToUWDecision.set(false);
                this.updateEqbdAccordionDisableState();
                if (showLoader) {
                    this.stopLoading();
                }
            }
        });
    }

    loadSavedEQBDCoverageDetails(showLoader: boolean = false): void {
        let fetch$: Observable<any> | null = null;
        if (this.authService.isPolicyType()) {
            const policyId = this.authService.getPolicyIdAsNumber();
            if (policyId) {
                fetch$ = this.indicationQuoteService.GetPolicyEQBDCoverages(policyId);
            }
        } else if (this.isIndicationType()) {
            const indicationId = Number(this.indicationId());
            if (indicationId) {
                fetch$ = this.indicationQuoteService.GetIndicationEQBDCoverages(indicationId);
            }
        } else {
            const quoteId = Number(this.quoteId() ?? sessionStorage.getItem("quoteId"));
            const versionId = Number(sessionStorage.getItem("versionNo") || 0);
            if (quoteId) {
                fetch$ = this.indicationQuoteService.GetQuoteEQBDCoverages(quoteId, versionId);
            }
        }

        if (!fetch$) {
            return;
        }
        if (showLoader) {
            this.startLoading();
        }

        fetch$.subscribe({
            next: (response: any) => {
                const data = response?.data ?? response;
                const coverageId = Number(data?.coverageId ?? 0);
                const isSelected = Number(data?.isEQBDSelected ?? 0) === 1;
                this.eqbdCoverageId.set(isNaN(coverageId) ? 0 : coverageId);
                if (!this.isEqbdDeclined()) {
                    this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: isSelected }, { emitEvent: false });
                } else {
                    this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: false }, { emitEvent: false });
                }
                if (showLoader) {
                    this.stopLoading();
                }
            },
            error: () => {
                this.eqbdCoverageId.set(0);
                if (showLoader) {
                    this.stopLoading();
                }
            }
        });
    }

    addTaxFeeItem(): void {
        if (!this.canAddTaxFeeItem()) {
            return;
        }
        const flagNewRowForRecalculate = this.hasAnyTaxFeesRecalculateMessage();
        this.slTaxFeeItems.push(this.createTaxFeeGroup());
        this.taxFeesRecalculateSltfRows.update((rows) => [...rows, flagNewRowForRecalculate]);
        this.syncAllTaxFeeSelections();
    }

    removeTaxFeeItem(index: number): void {
        if ( index >= this.slTaxFeeItems.length || this.isFeeSectionDisabled()) {
            return;
        }
        this.slTaxFeeItems.removeAt(index);
        this.taxFeesRecalculateSltfRows.update((rows) => {
            const next = [...rows];
            next.splice(index, 1);
            return next;
        });
        this.syncAllTaxFeeSelections();
    }

    canAddTaxFeeItem(): boolean {
        if (this.isFeeSectionDisabled()) {
            return false;
        }
        // If no items exist, allow adding the first one
        if (this.slTaxFeeItems.length === 0) {
            return true;
        }
        // Check if the last item has both description and amount filled
        const lastIndex = this.slTaxFeeItems.length - 1;
        const lastGroup = this.slTaxFeeItems.at(lastIndex) as FormGroup;
        if (!lastGroup) {
            return false;
        }
        const slFeeId = lastGroup.get('slFeeId')?.value;
        const feeAmount = lastGroup.get('feeAmount')?.value;
        const amt = this.parseAmount(feeAmount);
        return !!(slFeeId && slFeeId !== '' && feeAmount !== '' && feeAmount !== null && feeAmount !== undefined && amt > 0);
    }

    onTaxFeeDescriptionChange(index: number, event: Event): void {
        const group = this.slTaxFeeItems.at(index) as FormGroup;
        const target = event.target as HTMLSelectElement;
        const selectedValue = target?.value ?? '';
        const selectedOption = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(selectedValue));
        group.patchValue({
            slFeeId: selectedValue,
            slFeeDescription: selectedOption?.description || ''
        });
        this.handleOtherDescriptionValidator(group, this.isOtherFeeOption(selectedValue));
    }

    shouldShowOtherDescription(index: number): boolean {
        const group = this.slTaxFeeItems.at(index) as FormGroup;
        return this.isOtherFeeOption(group?.get('slFeeId')?.value);
    }

    handleOtherDescriptionValidator(group: FormGroup, shouldRequire: boolean): void {
        const control = group.get('otherDescription');
        if (!control) {
            return;
        }
        if (shouldRequire) {
            control.setValidators([Validators.required, noWhitespaceValidator]);
        } else {
            control.clearValidators();
            control.setValue('', { emitEvent: false });
        }
        control.updateValueAndValidity();
    }

    isOtherFeeOption(feeId: number | string): boolean {
        if (!feeId) {
            return false;
        }
        const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(feeId));
        if (!option) {
            return false;
        }
        return option.description?.toLowerCase() === 'other' || option.code?.toLowerCase() === 'other';
    }

    isFeeOptionDisabled(optionId: number, currentIndex: number): boolean {
        if (this.isOtherFeeOptionId(Number(optionId))) {
            return false;
        }
        const selectedIds = this.slTaxFeeItems.controls
            .map((group, index) => index === currentIndex ? null : Number(group.get('slFeeId')?.value))
            .filter((value) => value !== null && value !== undefined && !this.isOtherFeeOptionId(Number(value)));
        return selectedIds.includes(Number(optionId));
    }

    loadSurplusLinesTaxFees(showLoader: boolean = false): void {
        if (showLoader) {
            this.startLoading();
        }
        this.indicationQuoteService.GetSurplusLinesTaxFee().subscribe({
            next: (response: any) => {
                const options = Array.isArray(response) ? response.filter((item: any) => !!item) : [];
                
                // Find and store gencodeIds for Policy Fee and Surplus Lines Tax
                const policyFeeOption = options.find((item: any) => 
                    item.description?.toLowerCase() === 'policy fee' || item.code?.toLowerCase() === 'policy fee'
                );
                const surplusLinesTaxOption = options.find((item: any) => 
                    item.description?.toLowerCase() === 'surplus lines tax' || item.code?.toLowerCase() === 'surplus lines tax'
                );
                
                if (policyFeeOption) {
                    this.policyFeeGencodeId.set(Number(policyFeeOption.gencodeId));
                }
                if (surplusLinesTaxOption) {
                    this.surplusLinesTaxGencodeId.set(Number(surplusLinesTaxOption.gencodeId));
                }
                
                // Filter out Policy Fee and Surplus Lines Tax from dropdown options
                const filteredOptions = options.filter((item: any) => {
                    const desc = item.description?.toLowerCase() || '';
                    const code = item.code?.toLowerCase() || '';
                    return desc !== 'policy fee' && code !== 'policy fee' && 
                           desc !== 'surplus lines tax' && code !== 'surplus lines tax';
                });
                
                this.surplusLinesTaxFeeOptions.set(filteredOptions);
                this.fetchSurplusFeeDetails(showLoader);
            },
            error: () => {
                this.toastService.addNotification({message: "Failed to load Surplus Lines tax/fee list. Please retry.", type: ToastType.Error});
                this.fetchSurplusFeeDetails(showLoader);
            }
        });
    }

    fetchSurplusFeeDetails(showLoader: boolean = false): void {
        let fetch$: Observable<any> | null = null;
        if (this.authService.isPolicyType()) {
            const policyId = this.authService.getUserDetailsFromStorage()?.policyId;
            if (policyId) {
                fetch$ = this.indicationQuoteService.GetPolicySLFeeDetails(Number(policyId));
            }
        } else if (this.isIndicationType()) {
            const indicationId = Number(this.indicationId());
            if (indicationId) {
                fetch$ = this.indicationQuoteService.GetIndicationSLFeeDetails(indicationId);
            }
        } else {
            const quoteId = Number(this.quoteId() ?? sessionStorage.getItem("quoteId"));
            if (quoteId) {
                fetch$ = this.indicationQuoteService.GetQuoteSLFeeDetails(quoteId);
            }
        }

        if (!fetch$) {
            this.setSurplusFeeDetails([]);
            if (showLoader) {
                this.stopLoading();
            }
            return;
        }

        fetch$.subscribe({
            next: (response: any) => {
                const data = Array.isArray(response?.slTaxFeeDetails) ? response.slTaxFeeDetails :
                    (Array.isArray(response) ? response : []);
                this.setSurplusFeeDetails(data);
                if (this.pendingResetFeesOnSurplusLoadDueToTargetPremium) {
                    this.pendingResetFeesOnSurplusLoadDueToTargetPremium = false;
                    this.resetSurplusTaxFeeAmounts();
                    this.showTaxFeesRecalculateInlineValidation('targetPremium');
                }
                if (showLoader) {
                    this.stopLoading();
                }
            },
            error: () => {
                this.pendingResetFeesOnSurplusLoadDueToTargetPremium = false;
                this.setSurplusFeeDetails([]);
                if (showLoader) {
                    this.stopLoading();
                }
            }
        });
    }

    private resetSurplusTaxFeeAmounts(): void {
        this.acceptAndIssueForm.patchValue({ policyFee: '0', surplusLinesTax: '0' }, { emitEvent: false });
        this.acceptAndIssueForm.get('policyFee')?.updateValueAndValidity({ emitEvent: false });
        this.acceptAndIssueForm.get('surplusLinesTax')?.updateValueAndValidity({ emitEvent: false });
        this.slTaxFeeItems.controls.forEach((ctrl) => {
            const g = ctrl as FormGroup;
            const feeAmount = g.get('feeAmount');
            feeAmount?.setValue('0', { emitEvent: false });
            feeAmount?.updateValueAndValidity({ emitEvent: false });
        });
    }

    private showTaxFeesRecalculateInlineValidation(source: 'checkbox' | 'targetPremium'): void {
        this.taxFeesRecalculatePromptSource.set(source);
        this.taxFeesRecalculatePolicyFee.set(true);
        this.taxFeesRecalculateSurplusTax.set(true);
        this.taxFeesRecalculateSltfRows.set(Array(this.slTaxFeeItems.length).fill(true));
    }

    private syncRecalculatePromptSourceIfCleared(): void {
        if (!this.hasAnyTaxFeesRecalculateMessage()) {
            this.taxFeesRecalculatePromptSource.set(null);
        }
    }

    private clearTaxFeesRecalculateSltfRow(index: number): void {
        this.taxFeesRecalculateSltfRows.update((rows) => {
            if (index < 0 || index >= rows.length) {
                return rows;
            }
            const next = [...rows];
            next[index] = false;
            return next;
        });
    }

    private normalizeAmountControlToTwoDecimals(control: AbstractControl | null | undefined): void {
        if (!control) {
            return;
        }
        const raw = control.value;
        if (raw === null || raw === undefined || String(raw).trim() === '') {
            return;
        }
        const numeric = this.parseAmount(raw);
        if (isNaN(numeric)) {
            return;
        }
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numeric);
        control.setValue(formatted, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
    }

    onPolicyFeeBlur(): void {
        const control = this.acceptAndIssueForm.get('policyFee');
        this.normalizeAmountControlToTwoDecimals(control);
        control?.markAsTouched();
    }

    onSurplusLinesTaxBlur(): void {
        const control = this.acceptAndIssueForm.get('surplusLinesTax');
        this.normalizeAmountControlToTwoDecimals(control);
        control?.markAsTouched();
    }

    onSltfFeeAmountBlur(index: number): void {
        const g = this.slTaxFeeItems.at(index) as FormGroup;
        const control = g.get('feeAmount');
        this.normalizeAmountControlToTwoDecimals(control);
        control?.markAsTouched();
    }

    fetchIndicationCoverageSummaryDetails(showLoader: boolean = false): void {
        const indicationId = Number(this.indicationId());
        const branchId = Number(this.branchId());
        if (showLoader) {
            this.startLoading();
        }
        this.indicationQuoteService.fetchIndicationCoverageSummaryDetails(indicationId, branchId).subscribe({
            next: (response: any) => {  
                const inspectionFee = Number(response?.result?.inspectionFee ?? 0);
                    this.inspectionFeeFromCoverage.set(isNaN(inspectionFee) ? 0 : inspectionFee);
                    if (showLoader) {
                        this.stopLoading();
                    }
                },
            error: () => {
                this.inspectionFeeFromCoverage.set(0);
                if (showLoader) {
                    this.stopLoading();
                }
                }
            });
            }

    private loadQuoteCoverageThenSurplusFees(showLoader: boolean = false): void {
        const quoteId = Number(this.quoteId());
        const branchId = Number(this.branchId());
        if (!quoteId || !branchId) {
            this.loadSurplusLinesTaxFees(showLoader);
            return;
        }
        if (showLoader) {
            this.startLoading();
        }
        this.indicationQuoteService.fetchQuoteCoverageSummaryDetails(quoteId, branchId).subscribe({
            next: (response: any) => {
                const results = response?.result;
                let mismatch = false;
                if (Array.isArray(results) && results.length > 0) {
                    const sorted = [...results].sort((a, b) => Number(a.versionNo) - Number(b.versionNo));
                    const latest = sorted[sorted.length - 1];
                    const inspectionFee = Number(latest?.inspectionFee ?? 0);
                    this.inspectionFeeFromCoverage.set(isNaN(inspectionFee) ? 0 : inspectionFee);
                    if (results.length >= 2) {
                        const previous = sorted[sorted.length - 2];
                        mismatch = Number(latest?.targetPremium ?? 0) !== Number(previous?.targetPremium ?? 0);
                    }
                } else {
                    this.inspectionFeeFromCoverage.set(0);
                }
                this.pendingResetFeesOnSurplusLoadDueToTargetPremium = mismatch;
                if (showLoader) {
                    this.stopLoading();
                }
                this.loadSurplusLinesTaxFees(showLoader);
            },
            error: () => {
                this.pendingResetFeesOnSurplusLoadDueToTargetPremium = false;
                this.inspectionFeeFromCoverage.set(0);
                if (showLoader) {
                    this.stopLoading();
                }
                this.loadSurplusLinesTaxFees(showLoader);
            }
        });
    }

    setSurplusFeeDetails(bislFeeDetails: any[]): void {
        const feeArray = this.slTaxFeeItems;
        feeArray.clear();
        
        let policyFeeAmount = '';
        let surplusLinesTaxAmount = '';
        const otherFees: any[] = [];
        
        if (bislFeeDetails && bislFeeDetails.length > 0) {
            bislFeeDetails.forEach((detail: any) => {
                const feeAmount = detail?.feeAmount !== undefined && detail?.feeAmount !== null ? detail?.feeAmount : '';
                const formattedAmount = feeAmount !== '' && !isNaN(parseFloat(String(feeAmount))) 
                    ? new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(parseFloat(String(feeAmount)))
                    : '';
                
                const description = detail?.slFeeDescription?.toLowerCase() || '';
                const slFeeId = Number(detail?.slFeeId);
                
                // Check if it's Policy Fee or Surplus Lines Tax
                if (description === 'policy fee' || slFeeId === this.policyFeeGencodeId()) {
                    policyFeeAmount = formattedAmount;
                } else if (description === 'surplus lines tax' || slFeeId === this.surplusLinesTaxGencodeId()) {
                    surplusLinesTaxAmount = formattedAmount;
                } else {
                    // It's another fee type
                    otherFees.push({
                        id: detail?.id ?? 0,
                        slFeeId: detail?.slFeeId ?? '',
                        slFeeDescription: detail?.slFeeDescription ?? '',
                        otherDescription: detail?.otherDescription ?? '',
                        feeAmount: formattedAmount
                    });
                }
            });
        }
        
        // Set Policy Fee and Surplus Lines Tax values
        if (policyFeeAmount) {
            this.acceptAndIssueForm.patchValue({ policyFee: policyFeeAmount }, { emitEvent: false });
            this.acceptAndIssueForm.get('policyFee')?.updateValueAndValidity({ emitEvent: false });
        }
        if (surplusLinesTaxAmount) {
            this.acceptAndIssueForm.patchValue({ surplusLinesTax: surplusLinesTaxAmount }, { emitEvent: false });
            this.acceptAndIssueForm.get('surplusLinesTax')?.updateValueAndValidity({ emitEvent: false });
        }
        
        // Add other fees to the form array
        if (otherFees.length > 0) {
            otherFees.forEach((fee) => {
                feeArray.push(this.createTaxFeeGroup(fee));
            });
        }
        // No default item - users must click "Add tax/fee Item" to add items

        this.taxFeesRecalculatePolicyFee.set(false);
        this.taxFeesRecalculateSurplusTax.set(false);
        this.taxFeesRecalculateSltfRows.set(Array(this.slTaxFeeItems.length).fill(false));
        this.taxFeesRecalculatePromptSource.set(null);
        
        this.syncAllTaxFeeSelections();
    }

    syncAllTaxFeeSelections(): void {
        this.slTaxFeeItems.controls.forEach((_, index) => this.syncTaxFeeSelectionMetadata(index));
    }

    syncTaxFeeSelectionMetadata(index: number): void {
        const group = this.slTaxFeeItems.at(index) as FormGroup;
        if (!group) {
            return;
        }
        const feeId = group.get('slFeeId')?.value;
        const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(feeId));
        if (option) {
            group.get('slFeeDescription')?.setValue(option.description, { emitEvent: false });
        }
        this.handleOtherDescriptionValidator(group, this.isOtherFeeOption(feeId));
    }

    transformSurplusFeeData(data: any[], ownerId: number) {
        const slTaxFeeDetails: any[] = [];
        
        // Add Policy Fee if it has a value
        const policyFeeValue = this.acceptAndIssueForm.get('policyFee')?.value;
        if (policyFeeValue && policyFeeValue !== '' && this.policyFeeGencodeId()) {
            slTaxFeeDetails.push({
                id: ownerId,
                slFeeId: this.policyFeeGencodeId(),
                slFeeDescription: 'Policy Fee',
                otherDescription: '',
                feeAmount: Number(this.parseAmount(policyFeeValue).toFixed(2))
            });
        }
        
        // Add Surplus Lines Tax if it has a value
        const surplusLinesTaxValue = this.acceptAndIssueForm.get('surplusLinesTax')?.value;
        if (surplusLinesTaxValue && surplusLinesTaxValue !== '' && this.surplusLinesTaxGencodeId()) {
            slTaxFeeDetails.push({
                id: ownerId,
                slFeeId: this.surplusLinesTaxGencodeId(),
                slFeeDescription: 'Surplus Lines Tax',
                otherDescription: '',
                feeAmount: Number(this.parseAmount(surplusLinesTaxValue).toFixed(2))
            });
        }
        
        // Add other fee details from the form array
        if (data && data.length > 0) {
            const otherFees = data
                .filter((item) => item.slFeeId && item.feeAmount !== '' && item.feeAmount !== null && item.feeAmount !== undefined)
                .map((item) => ({
                    id: ownerId,
                    slFeeId: Number(item.slFeeId),
                    slFeeDescription: item.slFeeDescription?.trim() || '',
                    otherDescription: (this.isOtherFeeOption(item.slFeeId) || item.slFeeDescription?.toLowerCase() === 'other')
                        ? (item.otherDescription?.trim() || '')
                        : '',
                    feeAmount: Number(this.parseAmount(item.feeAmount).toFixed(2))
                }));
            slTaxFeeDetails.push(...otherFees);
        }
        
        return { slTaxFeeDetails };
    }

    persistSurplusFeeDetails(): Observable<any> {
        if (this.authService.isPolicyType()) {
            return of(true);
        }
        const currentUserId = this.userId();
        if (!currentUserId) {
            return of(true);
        }
        if (this.isIndicationType()) {
            const indicationId = Number(this.indicationId());
            if (!indicationId) {
                return of(true);
            }
            const payload = this.transformSurplusFeeData(this.slTaxFeeItems.getRawValue(), indicationId);
            return this.indicationQuoteService.SaveIndicationSLFeeDetails(indicationId, currentUserId, payload);
        }
        const quoteId = Number(this.quoteId() ?? sessionStorage.getItem("quoteId"));
        if (!quoteId) {
            return of(true);
        }
        const payload = this.transformSurplusFeeData(this.slTaxFeeItems.getRawValue(), quoteId);
        return this.indicationQuoteService.SaveQuoteSLFeeDetails(quoteId, currentUserId, payload);
    }

    private buildEQBDCoveragePayload(currentUserId: number): any {
        return {
            isEQBDSelected: this.acceptAndIssueForm.get('addEquipmentBreakdown')?.value ? 1 : null,
            coverageId: this.eqbdCoverageId(),
            eqbdPremium: this.eqbdAdditionalPremium(),
            userId: currentUserId
        };
    }

    persistEQBDCoverageDetails(): Observable<any> {
        if (this.authService.isPolicyType()) {
            return of(true);
        }
        const currentUserId = this.userId();
        if (!currentUserId) {
            return of(true);
        }
        const payload = this.buildEQBDCoveragePayload(currentUserId);
        if (this.isIndicationType()) {
            const indicationId = Number(this.indicationId());
            if (!indicationId) {
                return of(true);
            }
            return this.indicationQuoteService.SaveIndicationEQBDCoverages(indicationId, payload);
        }
        const quoteId = Number(this.quoteId() ?? sessionStorage.getItem("quoteId"));
        const versionId = Number(sessionStorage.getItem("versionNo") || 0);
        if (!quoteId) {
            return of(true);
        }
        return this.indicationQuoteService.SaveQuoteEQBDCoverages(quoteId, versionId, payload);
    }

    persistAllFeeDetails(): Observable<any> {
        return forkJoin([
            this.persistSurplusFeeDetails(),
            this.persistEQBDCoverageDetails()
        ]);
    }

    getCoveragePartEquipmentBreakdownPremium(): number {
        return this.acceptAndIssueForm.get('addEquipmentBreakdown')?.value ? this.eqbdAdditionalPremium() : 0;
    }

    getTotalPolicyPremium(): number {
        let total =
            this.commercialPropertyPremium()
            + this.inspectionFeeFromCoverage()
            + this.getCoveragePartEquipmentBreakdownPremium();
        total += this.parseAmount(this.acceptAndIssueForm.get('policyFee')?.value);
        total += this.parseAmount(this.acceptAndIssueForm.get('surplusLinesTax')?.value);
        this.slTaxFeeItems.controls.forEach((ctrl) => {
            const g = ctrl as FormGroup;
            total += this.parseAmount(g.get('feeAmount')?.value);
        });
        return total;
    }

    markAllTaxFeeControlsAsTouched(): void {
        this.slTaxFeeItems.controls.forEach((control) => {
            control.markAllAsTouched();
        });
    }

    ngOnInit(): void {
        this.authService.waitForSessionCachesRestored().subscribe(() => {
            this.initializeAfterCacheHydration();
        });
    }

    private initializeAfterCacheHydration(): void {
        
        this.loadEQBDOccupancyDetails(true);
        this.loadEQBDReferralDetails(true);
        
        this.navLogoClickSubscription = this.navigationHandler.logoClick$.subscribe(() => {
            this.onSaveIndicationHomeAction();
        });
        this.targetPremiumEmitter.emit(true);
        const storageData = this.authService.getUserDetailsFromStorage();
        this.storageData.set(storageData);
        this.isIndicationType() ? this.indicationId.set(storageData?.indicationId) : this.quoteId.set(storageData.quoteId);
        this.branchId.set(storageData?.branchId);
        this.userId.set(storageData?.userDetails?.userId);
        this.onCheckIsUWAdmin(storageData);
        const indicationQuoteId = this.isIndicationType() ? this.indicationId() : this.quoteId();
        const userRole = storageData.userRole ? Number(storageData.userRole) : null;
        const terms = sessionStorage.getItem("terms")     
        const irStatus = String(sessionStorage.getItem("inceptionStatus"))
        const quoteStatus = sessionStorage.getItem("statusRagCall");
        const isReferral = Number(sessionStorage.getItem("isreferral"))
        if(irStatus === "IDR" && isReferral === 1){
            this.isInceptionReferralLock.set(true)            
        }else{
            this.isInceptionReferralLock.set(false)
        }      
        
        this.handleSaveStep();               
        if (this.isIndicationType()) {
            this.fetchIndicationCoverageSummaryDetails(true);
            this.loadSurplusLinesTaxFees(true);
        } else {
            this.loadQuoteCoverageThenSurplusFees(true);
        }
        this.addEquipmentBreakdownSubscription = this.acceptAndIssueForm
            .get('addEquipmentBreakdown')
            ?.valueChanges.subscribe(() => {
                if (this.isFeeSectionDisabled()) {
                    return;
                }
                this.resetSurplusTaxFeeAmounts();
                this.showTaxFeesRecalculateInlineValidation('checkbox');
            });
        if((userRole === 1 && terms === 'true' && !this.isIndicationType() && quoteStatus === "Quote - Ready to Bind") || isReferral === 1){
            this.acceptAndIssueForm.patchValue({
                termsAndCondition: true,
                MaterialFactsStatement: true,
                TaxNoticeStatement: true,
                ClaimsDamageStatement: true
            });
            this.getQuoteClearanceData();
            this.enableViewAndDownloadDocumentSignal.set(true);          
            this.acceptAndIssueForm.disable(); // disable the form
            this.quoteResponseMessage.set('Quote issued successfully');
            this.proceedToRequestBindStep.set(true);
            this.requestBindEmitter.emit(true); // for showing Quote Status
            const quoteId =  Number(sessionStorage.getItem("quoteId"))
            this.getQuoteRiskSummary(quoteId)
        }
        if(this.authService.isPolicyType()){
            this.acceptAndIssueForm.patchValue({
                termsAndCondition: true,
                MaterialFactsStatement: true,
                TaxNoticeStatement: true,
                ClaimsDamageStatement: true
            });
            this.getPolicyCoverageSummaryDetails();
            this.enableViewAndDownloadDocumentSignal.set(false);
            const isReviewProcess = JSON.parse(sessionStorage.getItem('isReview') || 'false');
      isReviewProcess === true ? this.isReadOnly.set(true) : this.isReadOnly.set(false);
        }else{
            if(userRole === 2 && !this.isIndicationType()){ // UWAdmin && quoteType
                this.acceptAndIssueForm.patchValue({
                    termsAndCondition: true,
                    MaterialFactsStatement: true,
                    TaxNoticeStatement: true,
                    ClaimsDamageStatement: true
                });
            }
            if(storageData?.userDetails && storageData?.userDetails?.userId){
                this.userId.set(storageData?.userDetails?.userId);
                this.indicationQuoteId.set(indicationQuoteId);
            }
        }
        this.loadSavedEQBDCoverageDetails(true);
    }

    ngOnDestroy(): void {
        if(this.navLogoClickSubscription){
            this.navLogoClickSubscription.unsubscribe();
        }
        this.addEquipmentBreakdownSubscription?.unsubscribe();
    }
    

     onCheckIsUWAdmin(storageData: GetUserDetails) {
        const isUWAdmin = storageData?.userRole ? Number(storageData?.userRole) === 2 : false;
        if(isUWAdmin){
            this.acceptAndIssueForm.disable();
        }
    }

    getQuoteRiskSummary(quoteId: number){
        this.indicationQuoteService.getQuoteRiskSummary(quoteId).subscribe({
            next:(response) => {
                this.indicationRiskSummaryDetailsEmitter.emit(response);  
                sessionStorage.setItem("quoteNo",response.quoteNo)
            }
        })

        }

        getPolicyCoverageSummaryDetails(){
            const policyId = this.authService.getUserDetailsFromStorage()?.policyId;
            const branchId = Number(this.branchId());
            if(policyId){
                this.policyService.getPolicyCoverageSummary(Number(policyId),branchId).subscribe({
                    next: (response: any) => {
                        const inspectionFee = Number(response?.result?.inspectionFee ?? 0);
                        this.inspectionFeeFromCoverage.set(isNaN(inspectionFee) ? 0 : inspectionFee);
                    }
                })
            }
        }
    
    updateQuoteDate(){
        this.indicationQuoteService.UpdateQuoteData(Number(this.indicationQuoteId())).subscribe({
             next: (response: HttpResponse<any>) => {
                this.getQuoteRiskSummary(Number(this.indicationQuoteId()));
            },
            error: (error) => {
            }        
    })
    }

    submitAndIssueQuote() {
        if (this.hasAnyTaxFeesRecalculateMessage()) {
            this.acceptAndIssueForm.markAllAsTouched();
            this.markAllTaxFeeControlsAsTouched();
            return;
        }
        if (this.acceptAndIssueForm.invalid) {
            this.acceptAndIssueForm.markAllAsTouched();
            this.markAllTaxFeeControlsAsTouched();
            return;
        }
        this.persistAllFeeDetails().subscribe({
            next: () => {
                this.issueQuoteFlow();
            },
            error: () => {
                this.toastService.addNotification({message: "System error. Failed to save EQBD/Surplus Lines details. Please retry.", type: ToastType.Error});
            }
        });
    }

    issueQuoteFlow() {
        if(this.userId() && this.indicationQuoteId() && this.isIndicationType()) {
            this.indicationQuoteService.submitAndIssueQuote(Number(this.indicationQuoteId()), Number(this.userId())).subscribe({
                next: (response: HttpResponse<any>) => {
                    if (response && response.body) {
                        const quoteId = response.body.result.result;
                        sessionStorage.setItem("quoteId",quoteId);
                        sessionStorage.setItem("quoteType","quote");
                        this.quoteId.set(quoteId);
                        const status = sessionStorage.getItem("statusRagCall");                        
                        
                       
                        
                    }
                    if(response.status === 200){
                        const quoteId =  Number(sessionStorage.getItem("quoteId"));
                        this.enableViewAndDownloadDocumentSignal.set(true);
                        this.getQuoteClearanceData();
                        this.acceptAndIssueForm.disable(); // disable the form
                        this.quoteResponseMessage.set('Quote issued successfully');
                        this.proceedToRequestBindStep.set(true);
                        this.requestBindEmitter.emit(true); // for showing Quote Status
                        this.getQuoteRiskSummary(quoteId);
                        this.requestQuoteService.onUpdateTargetPremium(true);                        
                    }
                },
                error: () => {
                    this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                }
            });
        }else{
            this.updateDownloadEmailCompleteFlag();
            this.enableViewAndDownloadDocumentSignal.set(true);
            this.getQuoteClearanceData();
            this.acceptAndIssueForm.disable(); // disable the form
            this.quoteResponseMessage.set('Quote issued successfully');
            this.proceedToRequestBindStep.set(true);
            this.requestBindEmitter.emit(true); // for showing Quote Status
            const quoteId =  Number(sessionStorage.getItem("quoteId"));
            
            this.updateQuoteDate();
        }

    }


        updateDownloadEmailCompleteFlag() {
        const storageData = this.authService.getUserDetailsFromStorage();
        const quoteId = this.authService.isPolicyType() 
            ? storageData?.policyId 
            : storageData?.quoteId;
        const userId = storageData?.userDetails?.userId;
        
        const versionNo = Number(sessionStorage.getItem("versionNo"));
        
        if (quoteId && userId && versionNo) {
            this.quoteDocumentService.UpdateIsDownloadEmailComplete(
                Number(quoteId),
                Number(userId),
                versionNo,
                0
            ).subscribe({
                next: (response: any) => {
                    // Flag updated successfully - emit event to parent component
                    this.checkDownloadEmailCompleteStatus(versionNo);
                    
                },
                error: (error: any) => {
                    // Handle error silently or log if needed
                }
            });
        }
    }

  onViewandDownloadDocumentAction(type : string, fromPopup: boolean = false) {
        if(this.isDownloadEmailComplete() === 1) {
            return; // Prevent action if already completed
        }
        // Set flag to track if action is from popup
        this.isActionFromPopup.set(fromPopup);
        
        if(type === 'download'){
            this.quoteDocumentService.setDownloadQuoteDocument(true); // trigger the quote document process
        }else{
            this.quoteDocumentService.setEmailQuoteDocument(true);
        }
    }
    
    initializeVersionNo() {
        const versionNo = sessionStorage.getItem("versionNo");
        
        if (versionNo) {
            this.versionNo.set(Number(versionNo));
        }
    }

    checkDownloadEmailCompleteStatus(versionNo?: number) {
        const quoteId = this.quoteId();
        
        if (quoteId && versionNo) {
            this.startLoading();
            this.quoteDocumentService.getIsDownloadEmailComplete(Number(quoteId), Number(versionNo)).subscribe({
                next: (response: any) => {
                    if (response && response.isDownloadEmailComplete !== undefined) {
                        this.isDownloadEmailComplete.set(response.isDownloadEmailComplete);
                    }
                    this.stopLoading();
                },
                error: (error: any) => {
                    this.stopLoading();
                    // Handle error silently or log if needed
                }
            });
        } else {
            // If quoteId or versionNo is missing, ensure loading is set to false
            if (this.loadingCounter === 0) {
                this.isLoading.set(false);
            }
        }
    }

    onDownloadEmailComplete() {
        // Update the signal immediately when API returns 200
        this.isDownloadEmailComplete.set(1);
        
        // If action was initiated from popup, automatically navigate to next step
        if (this.isActionFromPopup()) {
            this.isActionFromPopup.set(false); // Reset flag
            this.proceedToNextStepAutomatically();
        }
    }

    proceedToNextStepAutomatically() {
        this.indicationQuoteService.requestBind().subscribe({
            next: (response) => {
                if(response){
                    sessionStorage.setItem("terms","false")
                    this.handleSaveStep();
                    this.proceedToNextStep.emit(this.requestBindStep());
                }
            }, 
            error: (error) => {
            }
        });
    }

    OnRequestBind(): void{
        // Check if quote document has been downloaded/emailed
        if (this.enableViewAndDownloadDocumentSignal() && this.isDownloadEmailComplete() !== 1) {
            const dialogRef = this.dialog.open(QuoteDocumentDialogComponent);
            
            dialogRef.afterClosed().subscribe((result: string) => {
                if (result === 'download') {
                    this.onViewandDownloadDocumentAction('download', true); // Pass true to indicate it's from popup
                } else if (result === 'email') {
                    this.onViewandDownloadDocumentAction('email', true); // Pass true to indicate it's from popup
                }
                // If result is 'cancel' or null, do nothing
            });
            return;
        }
        
        this.indicationQuoteService.requestBind().subscribe({
            next: (response) => {
                if(response){
                    sessionStorage.setItem("terms","false")
                    this.handleSaveStep();
                    this.proceedToNextStep.emit(this.requestBindStep());
                }
            }, 
            error: (error) => {
            }
        });
    }  

    onReturnToDashboardMTA() {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: { message: "Do you want to return to dashboard?" }
        });
        
        dialog.afterClosed().subscribe((result: boolean) => {
            if(result){
                const isQuoteMTA = sessionStorage.getItem('isQuoteMTA');
                if(isQuoteMTA){
                    this.policyService.declineMTAByBroker(Number(this.authService.getUserDetailsFromStorage()?.policyId), true)
                    .subscribe({
                        next: (response: any) => {
                            if(response){
                            this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                            }
                        },
                        error: (error: any) => {
                            this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                        }
                    })
                }else{
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            }
        }); 
    }

    onSaveIndicationHomeAction() {
        const irStatus = String(sessionStorage.getItem("inceptionStatus"));
        const isreferral = Number(sessionStorage.getItem("isreferral"))

        if(isreferral !== 1){
            if(this.authService.isPolicyType()){
                this.onReturnToDashboardMTA();
            }else{
                // Apply mandatory-field validation for Save & Exit as well
                if (this.hasAnyTaxFeesRecalculateMessage()) {
                    this.acceptAndIssueForm.markAllAsTouched();
                    this.markAllTaxFeeControlsAsTouched();
                    return;
                }
                if (this.acceptAndIssueForm.invalid) {
                    this.acceptAndIssueForm.markAllAsTouched();
                    this.markAllTaxFeeControlsAsTouched();
                    this.toastService.addNotification({
                        message: "Fill all the mandatory field before saving data",
                        type: ToastType.Error
                    });
                    return;
                }
                this.saveFeesAndEmitOnExit();
            }
        }else{
            this.router.navigate(['/quote']);
        }
    }

    handleSaveStep() {
        if(this.authService.isPolicyType()){
            this.onSaveStep("4b", "Policy", String(this.authService.getUserDetailsFromStorage()?.policyId));
        }else{
            if(this.isIndicationType()){
                this.onSaveStep("4b", "Indication", String(this.indicationId()));
            }else{
                this.onSaveStep("4b", "Quote", String(this.authService.getUserDetailsFromStorage()?.quoteId));
            }
        }
    }

    onSaveStep(step: string, type: string, id: string) {
        if (this.isReadOnly()) {
            return;
        }
        this.indicationQuoteService.saveStepForCurrentUser(step, type, id).subscribe({
            next: (response: any) => {
            },
            error: (error: any) => {
            }
        })
    }

    getQuoteClearanceData() {
        this.startLoading();
        const quoteId = this.quoteId();
        this.propertyService.getQuoteClearanceData(Number(quoteId)).subscribe({
            next: (response: any) => {
                this.propertyService.setVersionNo(response.quote?.versionNo);
                this.initializeVersionNo();
                this.refreshQuoteEQBDCoverageForSaveExit(Number(quoteId), Number(response.quote?.versionNo ?? 0));
                this.checkDownloadEmailCompleteStatus(response.quote?.versionNo); 
                this.stopLoading();
            },    
            error: (error: any) => {            
                this.stopLoading();
            }   
        })
        
    }

    private refreshQuoteEQBDCoverageForSaveExit(quoteId: number, versionNo: number): void {
        if (!quoteId || !versionNo) {
            return;
        }

        this.indicationQuoteService.GetQuoteEQBDCoverages(quoteId, versionNo).subscribe({
            next: (response: any) => {
                const data = response?.data ?? response;
                const coverageId = Number(data?.coverageId ?? 0);
                const isSelected = Number(data?.isEQBDSelected ?? 0) === 1;
                this.eqbdCoverageId.set(isNaN(coverageId) ? 0 : coverageId);

                if (!this.isEqbdDeclined()) {
                    this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: isSelected }, { emitEvent: false });
                } else {
                    this.acceptAndIssueForm.patchValue({ addEquipmentBreakdown: false }, { emitEvent: false });
                }
            },
            error: () => {
                // Keep existing form state if quote EQBD fetch fails.
            }
        });
    }

    isIndicationType(): boolean {
        const quoteType = this.authService.getUserDetailsFromStorage().quoteType;
        return quoteType === "indication";
    }

    private buildSavePayloadRequest(): any {
        const userDetails = this.authService.getUserDetailsFromStorage();
        if(this.isIndicationType()) {
            return {
                indicationId : userDetails.indicationId,
                branchId: userDetails.branchId,
                userId: userDetails.userDetails?.userId
            };
        }
        return {
            quoteId : userDetails.quoteId,                        
            branchId: userDetails.branchId,
            userId: userDetails.userDetails?.userId
        };
    }

    private saveFeesAndEmitOnExit(): void {
        const payloadRequest = this.buildSavePayloadRequest();
        this.saveIndicationHomeEmitter.emit({
            payloadRequest,
            persistFees: () => this.persistAllFeeDetails()
        });
    }
}

